import { useMutation } from "@apollo/client";

import {
  EventsApiResponse,
  UpdateEventFeedbackInput,
  UpdateEventResponse,
} from "../../../../common/types/events/api";
import { GetRecentEventsParams } from "../../../../common/types/events/params";
import { errorNotification } from "../../../../common/variables/notification";
import { UPDATE_EVENT_FEEDBACK } from "../../../../rest/mutations";
import { GET_RECENT_EVENTS } from "../../../../rest/queries";

export const useUpdateEventFeedback = (
  queryVariables: GetRecentEventsParams
) => {
  const [updateMutation, { loading, error, data }] = useMutation<
    { updateEventFeedback: UpdateEventResponse },
    { input: UpdateEventFeedbackInput }
  >(UPDATE_EVENT_FEEDBACK);

  const updateEventFeedback = async (input: UpdateEventFeedbackInput) => {
    try {
      const response = await updateMutation({
        variables: { input },
        context: {
          headers: {
            "Content-Type": "application/json",
          },
        },
        update: (cache, { data }) => {
          if (!data?.updateEventFeedback) return;

          // The original sanitization was too aggressive, removing empty strings ("")
          // which are valid parts of the cache key. This ensures we only remove
          // null or undefined values, aligning with how the cache key was created.
          const sanitizedQueryVariables = Object.entries(queryVariables).reduce<
            Record<string, any>
          >((acc, [key, value]) => {
            if (value !== null && value !== undefined) {
              acc[key] = value;

              if (typeof value === "string" && value.includes("#")) {
                if (value.includes("#L#")) {
                  acc[key] = value.split("#L#").at(-1);
                } else {
                  acc[key] = encodeURIComponent(value);
                }
              }
            }

            return acc;
          }, {}) as GetRecentEventsParams;

          try {
            // Use the same variables from the original query to read from the cache
            const cachedData = cache.readQuery<{
              events: EventsApiResponse;
            }>({
              query: GET_RECENT_EVENTS,
              variables: sanitizedQueryVariables,
            });

            if (!cachedData || !cachedData.events) {
              return;
            }

            const updatedEvents = {
              ...cachedData.events,
              data: cachedData.events.data.map(item => {
                if (item?.event?.id === input.sk) {
                  // To ensure the cache is consistent, update the values
                  // in both the top-level event object and the nested ddbRecord.
                  return {
                    ...item,
                    event: {
                      ...item.event,
                      feedback: input.feedback,
                      mitigatedRisk: input.mitigatedRisk?.split(";") ?? [],
                      ddbRecord: {
                        ...item.event.ddbRecord,
                        feedback: input.feedback,
                        mitigatedRisk: input.mitigatedRisk,
                      },
                    },
                  };
                }

                return item;
              }),
            };

            // Write back to the cache with the same query and variables
            cache.writeQuery({
              query: GET_RECENT_EVENTS,
              variables: sanitizedQueryVariables,
              data: { events: updatedEvents },
              overwrite: true,
            });
          } catch (error) {
            console.error("Error updating cache:", error);

            errorNotification();
          }
        },
      });

      return response.data?.updateEventFeedback;
    } catch (err) {
      console.error("Error updating event feedback:", err);

      errorNotification("Something went wrong");
    }
  };

  return {
    updateEventFeedback,
    loading,
    error,
    data: data?.updateEventFeedback,
  };
};
